import { memo, useCallback, useMemo, useState } from 'react'
import type { ChangeEvent, CSSProperties, MouseEvent } from 'react'
import { useGridList, useAgendaItems } from '../../hooks/useSchedule.js'
import { usePlayout } from '../../hooks/usePlayout.js'
import { useServerClock } from '../../hooks/useServerClock.js'
import {
  useActiveChannel,
  useActiveChannelId,
  useAlerts,
  useItems,
  useItemsLoading,
  usePlayoutState,
  useStore,
  useWsConnected,
} from '../../store/index.js'
import type { BugType, ScheduleItem, WatchdogAlert } from '../../types/index.js'
import {
  BUG_TYPE_LABELS,
  ITEM_STATUS_COLORS,
  formatDurationShort,
  formatTime,
  scheduleItemTitle,
} from '../../lib/utils.js'
import './MobileOperatorView.css'
import { Spinner } from '../common/Spinner.js'

type MobileFilter = 'ALL' | 'PROGRAM' | 'LIVE' | 'AD_BREAK' | 'ALERTS'
type MobileTab = 'antenna' | 'grid' | 'alerts'
type ConfirmAction = 'skip' | 'interlude'
type DynamicStyle = CSSProperties & { [key: `--${string}`]: string | number }

function itemTitle(item: ScheduleItem | null | undefined, fallback = '-') {
  return scheduleItemTitle(item, fallback) ?? fallback
}

function itemSubtitle(item: ScheduleItem | null | undefined) {
  if (!item) return ''
  if (item.itemType === 'LIVE') return item.liveSource?.sourceType === 'DECKLINK' ? 'Direct Decklink' : 'Direct Internet'
  if (item.itemType === 'AD_BREAK') return `${item.adPlacements?.length ?? 0} spot(s)`
  if (item.itemType === 'INFOSERVICE') return item.adPlacements?.[0]?.spot?.title ?? 'Infoservice'
  if (item.itemType === 'JINGLE') return 'Jingle / coming next'
  if (item.itemType === 'INTERLUDE' || item.itemType === 'FILLER') return 'Interlude'
  return item.program?.genre ?? item.program?.type ?? 'Programme'
}

function formatPreciseCountdown(targetIso: string | null | undefined, nowMs: number): string {
  if (!targetIso) return '--:--.---'
  const targetMs = new Date(targetIso).getTime()
  if (!Number.isFinite(targetMs)) return '--:--.---'
  const ms = Math.max(0, targetMs - nowMs)
  const totalSec = Math.floor(ms / 1000)
  const millis = Math.floor(ms % 1000)
  const sec = totalSec % 60
  const min = Math.floor(totalSec / 60) % 60
  const hrs = Math.floor(totalSec / 3600)
  const base = hrs > 0
    ? `${hrs}:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  return `${base}.${String(millis).padStart(3, '0')}`
}

function isFutureItem(item: ScheduleItem, nowMs: number) {
  return !['DONE', 'SKIPPED', 'ON_AIR'].includes(item.status) && new Date(item.startTime).getTime() > nowMs
}

function isActiveAlert(alert: WatchdogAlert) {
  return alert.severity !== 'OK'
}

function matchesFilter(item: ScheduleItem, filter: MobileFilter) {
  if (filter === 'ALL') return true
  if (filter === 'PROGRAM') return item.itemType === 'PROGRAM'
  if (filter === 'LIVE') return item.itemType === 'LIVE'
  if (filter === 'AD_BREAK') return item.itemType === 'AD_BREAK'
  return true
}

function matchesSearch(item: ScheduleItem, search: string) {
  const q = search.trim().toLowerCase()
  if (!q) return true
  const haystack = [
    itemTitle(item),
    itemSubtitle(item),
    item.program?.summary,
    item.program?.presenter,
    item.program?.genre,
    item.liveSource?.name,
    item.interludeClip?.name,
  ].filter(Boolean).join(' ').toLowerCase()
  return haystack.includes(q)
}

function statusLabel(status: ScheduleItem['status']) {
  const labels: Record<ScheduleItem['status'], string> = {
    SCHEDULED: 'Programmé',
    LOADING: 'Chargement',
    ON_AIR: 'Antenne',
    DONE: 'Fait',
    GAP: 'Interlude',
    ERROR: 'Erreur',
    SKIPPED: 'Passé',
  }
  return labels[status] ?? status
}

function stopSheetPropagation(event: MouseEvent<HTMLElement>) {
  event.stopPropagation()
}

export const MobileOperatorView = memo(function MobileOperatorView({ onExitMobile }: { onExitMobile: () => void }) {
  const activeChannelId = useActiveChannelId()
  const activeChannel = useActiveChannel()
  const playout = usePlayoutState()
  const alerts = useAlerts()
  const items = useItems()
  const itemsLoading = useItemsLoading()
  const wsConnected = useWsConnected()
  const clock = useServerClock()
  const { now, nowMs, offsetMs, rttMs, status: clockStatus } = clock
  const {
    endLiveAndFill,
    toggleInterlude,
    toggleBug,
    skipPending,
    interludePending,
    bugPending,
    activeBugType,
    manualInterludeActive,
  } = usePlayout(activeChannelId)

  useGridList(activeChannelId)
  useAgendaItems()

  const [activeTab, setActiveTab] = useState<MobileTab>('antenna')
  const [filter, setFilter] = useState<MobileFilter>('ALL')
  const [search, setSearch] = useState('')
  const [selectedItem, setSelectedItem] = useState<ScheduleItem | null>(null)
  const [controlsLocked, setControlsLocked] = useState(true)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()),
    [items],
  )

  const onAirItem = useMemo(() => {
    const explicit = sortedItems.find(item => item.status === 'ON_AIR')
    if (explicit) return explicit
    return sortedItems.find(item => {
      const start = new Date(item.startTime).getTime()
      const end = new Date(item.endTime).getTime()
      return nowMs >= start && nowMs < end
    }) ?? null
  }, [nowMs, sortedItems])

  const upcomingItems = useMemo(
    () => sortedItems.filter(item => isFutureItem(item, nowMs)),
    [nowMs, sortedItems],
  )

  const nextItem = upcomingItems[0] ?? null
  const nextProgram = useMemo(() => upcomingItems.find(item => item.itemType === 'PROGRAM') ?? null, [upcomingItems])
  const nextLive = useMemo(() => upcomingItems.find(item => item.itemType === 'LIVE') ?? null, [upcomingItems])
  const nextAdBreak = useMemo(() => upcomingItems.find(item => item.itemType === 'AD_BREAK') ?? null, [upcomingItems])
  const activeAlerts = useMemo(() => alerts.filter(isActiveAlert), [alerts])

  const filteredItems = useMemo(() => {
    if (filter === 'ALERTS') {
      const ids = new Set(activeAlerts.map(alert => alert.itemId).filter(Boolean))
      return sortedItems.filter(item => ids.has(item.id))
    }
    return sortedItems.filter(item => matchesFilter(item, filter) && matchesSearch(item, search))
  }, [activeAlerts, filter, search, sortedItems])

  const progress = useMemo(() => {
    if (!onAirItem) return { pct: 0, elapsedMs: 0, remainingMs: 0 }
    const startMs = new Date(onAirItem.startTime).getTime()
    const endMs = new Date(onAirItem.endTime).getTime()
    const elapsedMs = Math.max(0, nowMs - startMs)
    const remainingMs = Math.max(0, endMs - nowMs)
    const durationMs = Math.max(1, endMs - startMs)
    return {
      pct: Math.min(100, Math.max(0, (elapsedMs / durationMs) * 100)),
      elapsedMs,
      remainingMs,
    }
  }, [nowMs, onAirItem])

  const bridgeConnected = playout?.bridgeConnected ?? false
  const controlsDisabled = controlsLocked || !bridgeConnected
  const logoLabel = activeBugType ? BUG_TYPE_LABELS[activeBugType as BugType] ?? activeBugType : 'Logo'

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value), [])
  const handleSelectItem = useCallback((item: ScheduleItem) => setSelectedItem(item), [])
  const handleCloseItem = useCallback(() => setSelectedItem(null), [])
  const handleToggleControls = useCallback(() => setControlsLocked(value => !value), [])
  const handleToggleBug = useCallback(() => { void toggleBug() }, [toggleBug])
  const handleRequestSkip = useCallback(() => setConfirmAction('skip'), [])
  const handleRequestInterlude = useCallback(() => setConfirmAction('interlude'), [])
  const handleCancelConfirm = useCallback(() => setConfirmAction(null), [])
  const handleDismissAlert = useCallback((alert: WatchdogAlert) => {
    useStore.getState().dismissAlert(alert.type, alert.itemId)
  }, [])
  const handleClearAlerts = useCallback(() => {
    const store = useStore.getState()
    store.clearAlerts()
    store.markAlertsRead()
  }, [])

  const runConfirmedAction = useCallback(async () => {
    const action = confirmAction
    setConfirmAction(null)
    if (action === 'skip' && onAirItem) await endLiveAndFill(onAirItem.id, [], { compact: true })
    if (action === 'interlude') await toggleInterlude()
  }, [confirmAction, endLiveAndFill, onAirItem, toggleInterlude])

  const progressStyle = { '--mobile-progress': `${progress.pct}%` } as DynamicStyle

  return (
    <div className="mobile-operator">
      <header className="mobile-operator__header">
        <div className="mobile-operator__identity">
          <div className="mobile-operator__app-title">TrafficPlay</div>
          <div className="mobile-operator__channel-title">{activeChannel?.name ?? 'Chaîne'}</div>
        </div>
        <div className="mobile-operator__header-actions">
          <div className="mobile-operator__clock">
            <span className="mobile-operator__clock-time">
              {String(now.getHours()).padStart(2, '0')}:{String(now.getMinutes()).padStart(2, '0')}:{String(now.getSeconds()).padStart(2, '0')}
            </span>
            <span className="mobile-operator__clock-meta">
              {clockStatus === 'SYNCED' ? 'sync' : clockStatus.toLowerCase()} {offsetMs >= 0 ? '+' : ''}{Math.round(offsetMs)}ms
            </span>
          </div>
          <button type="button" onClick={onExitMobile} className="mobile-operator__ghost-button">Desktop</button>
        </div>
      </header>

      <main className="mobile-operator__body">
        <section className="mobile-operator__on-air" aria-labelledby="mobile-on-air-title">
          <div className="mobile-operator__status-line" aria-live="polite">
            <span className={`mobile-operator__connection-dot ${bridgeConnected ? 'is-connected' : 'is-disconnected'}`} aria-hidden="true" />
            <span>{bridgeConnected ? 'CasparCG connecté' : 'CasparCG déconnecté'}</span>
            <span className="mobile-operator__separator">/</span>
            <span>{wsConnected ? 'WebSocket OK' : 'WebSocket KO'}</span>
          </div>

          <div className="mobile-operator__on-air-card">
            <div className="mobile-operator__eyebrow">ON AIR</div>
            <h1 id="mobile-on-air-title" className="mobile-operator__on-air-title">{itemTitle(onAirItem, 'Aucune diffusion en cours')}</h1>
            {onAirItem && (
              <>
                <div className="mobile-operator__on-air-meta">
                  {itemSubtitle(onAirItem)} / {formatTime(onAirItem.startTime)} - {formatTime(onAirItem.endTime)}
                </div>
                <div className="mobile-operator__progress" aria-label={`Progression ${Math.round(progress.pct)} %`}>
                  <div className="mobile-operator__progress-fill" style={progressStyle} />
                </div>
                <div className="mobile-operator__progress-meta">
                  <span>{formatPreciseCountdown(onAirItem.endTime, nowMs)} avant fin</span>
                  <span>{formatDurationShort(onAirItem.durationSec)}</span>
                </div>
              </>
            )}
          </div>

          <div className="mobile-operator__counter-grid">
            <CounterCard label="Fin item" value={onAirItem ? formatPreciseCountdown(onAirItem.endTime, nowMs) : '--:--.---'} />
            <CounterCard label="Prog" value={nextProgram ? formatPreciseCountdown(nextProgram.startTime, nowMs) : '--:--.---'} title={itemTitle(nextProgram, '')} />
            <CounterCard label="Direct" value={nextLive ? formatPreciseCountdown(nextLive.startTime, nowMs) : '--:--.---'} title={itemTitle(nextLive, '')} tone="warning" />
          </div>

          <div className="mobile-operator__diagnostics">
            <Metric label="Runtime" value={playout?.runtimePhase ?? playout?.state ?? '-'} />
            <Metric label="Recovery" value={playout?.recoveryStatus ?? 'IDLE'} />
            <Metric label="Media" value={String(playout?.casparMediaPath ?? playout?.casparStatus?.casparMediaPath ?? '-')} />
            <Metric label="Logo" value={activeBugType ?? playout?.activeBugType ?? '-'} />
            <Metric label="Drift" value={typeof playout?.playoutDriftMs === 'number' ? `${Math.round(playout.playoutDriftMs)} ms` : '-'} />
            <Metric label="RTT" value={rttMs == null ? '-' : `${Math.round(rttMs)} ms`} />
          </div>
        </section>

        <nav className="mobile-operator__tabs" aria-label="Sections opérateur">
          <TabButton tab="antenna" label="Antenne" active={activeTab === 'antenna'} onSelect={setActiveTab} />
          <TabButton tab="grid" label="Grille" active={activeTab === 'grid'} onSelect={setActiveTab} />
          <TabButton tab="alerts" label={`Alertes${activeAlerts.length ? ` ${activeAlerts.length}` : ''}`} active={activeTab === 'alerts'} onSelect={setActiveTab} />
        </nav>

        {activeTab === 'antenna' && (
          <div className="mobile-operator__tab-body">
            <QuickAccess nextItem={nextItem} nextLive={nextLive} nextAdBreak={nextAdBreak} onSelect={handleSelectItem} />
            <UpcomingList items={upcomingItems.slice(0, 12)} loading={itemsLoading} onSelect={handleSelectItem} />
          </div>
        )}

        {activeTab === 'grid' && (
          <div className="mobile-operator__tab-body">
            <input
              type="search"
              value={search}
              onChange={handleSearchChange}
              placeholder="Recherche"
              aria-label="Rechercher dans la grille"
              className="mobile-operator__search"
            />
            <FilterBar filter={filter} onFilter={setFilter} alertCount={activeAlerts.length} />
            <ItemList items={filteredItems} loading={itemsLoading} onSelect={handleSelectItem} />
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="mobile-operator__tab-body">
            <AlertList alerts={activeAlerts} onDismiss={handleDismissAlert} onClear={handleClearAlerts} />
          </div>
        )}
      </main>

      <footer className="mobile-operator__controls" aria-label="Contrôles antenne">
        <button
          type="button"
          onClick={handleToggleControls}
          className={`mobile-operator__lock-button ${controlsLocked ? 'is-locked' : 'is-unlocked'}`}
          aria-pressed={!controlsLocked}
        >
          {controlsLocked ? 'Contrôles verrouillés' : 'Contrôles actifs'}
        </button>
        <div className="mobile-operator__control-grid">
          <button
            type="button"
            disabled={controlsDisabled || bugPending}
            onClick={handleToggleBug}
            className={`mobile-operator__control-button ${activeBugType ? 'is-active' : ''}`}
            title={logoLabel}
            aria-pressed={Boolean(activeBugType)}
          >
            {bugPending ? '...' : 'Logo'}
          </button>
          <button
            type="button"
            disabled={controlsDisabled || skipPending || !nextItem}
            onClick={handleRequestSkip}
            className="mobile-operator__control-button"
          >
            {skipPending ? '...' : 'Skip'}
          </button>
          <button
            type="button"
            disabled={controlsDisabled || interludePending}
            onClick={handleRequestInterlude}
            className={`mobile-operator__control-button ${manualInterludeActive ? 'is-active' : ''}`}
            aria-pressed={manualInterludeActive}
          >
            {interludePending ? '...' : 'Interlude'}
          </button>
        </div>
      </footer>

      {selectedItem && <ItemDetails item={selectedItem} onClose={handleCloseItem} />}

      {confirmAction && (
        <ConfirmSheet
          action={confirmAction}
          currentItem={onAirItem}
          nextItem={nextItem}
          manualInterludeActive={manualInterludeActive}
          onCancel={handleCancelConfirm}
          onConfirm={runConfirmedAction}
        />
      )}
    </div>
  )
})

const CounterCard = memo(function CounterCard({
  label,
  value,
  title,
  tone = 'info',
}: {
  label: string
  value: string
  title?: string
  tone?: 'info' | 'warning'
}) {
  const active = value !== '--:--.---'
  const className = ['mobile-operator__counter', active ? 'is-active' : '', tone === 'warning' ? 'is-warning' : ''].filter(Boolean).join(' ')
  return (
    <div className={className}>
      <div className="mobile-operator__counter-label">{label}</div>
      <div className="mobile-operator__counter-value">{value}</div>
      {title && <div className="mobile-operator__counter-title">{title}</div>}
    </div>
  )
})

const Metric = memo(function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="mobile-operator__metric">
      <span className="mobile-operator__metric-label">{label}</span>
      <span className="mobile-operator__metric-value">{value}</span>
    </div>
  )
})

const TabButton = memo(function TabButton({
  tab,
  label,
  active,
  onSelect,
}: {
  tab: MobileTab
  label: string
  active: boolean
  onSelect: (tab: MobileTab) => void
}) {
  const handleClick = useCallback(() => onSelect(tab), [onSelect, tab])
  return (
    <button type="button" onClick={handleClick} className={`mobile-operator__tab ${active ? 'is-active' : ''}`} aria-pressed={active}>
      {label}
    </button>
  )
})

const QuickCard = memo(function QuickCard({ label, item, onSelect }: {
  label: string
  item: ScheduleItem | null
  onSelect: (item: ScheduleItem) => void
}) {
  const handleClick = useCallback(() => {
    if (item) onSelect(item)
  }, [item, onSelect])
  return (
    <button type="button" disabled={!item} onClick={handleClick} className="mobile-operator__quick-card">
      <span className="mobile-operator__quick-label">{label}</span>
      <span className="mobile-operator__quick-title">{item ? itemTitle(item) : '-'}</span>
      <span className="mobile-operator__quick-meta">{item ? formatTime(item.startTime) : '-'}</span>
    </button>
  )
})

const QuickAccess = memo(function QuickAccess({
  nextItem,
  nextLive,
  nextAdBreak,
  onSelect,
}: {
  nextItem: ScheduleItem | null
  nextLive: ScheduleItem | null
  nextAdBreak: ScheduleItem | null
  onSelect: (item: ScheduleItem) => void
}) {
  return (
    <div className="mobile-operator__quick-grid">
      <QuickCard label="Suivant" item={nextItem} onSelect={onSelect} />
      <QuickCard label="Direct" item={nextLive} onSelect={onSelect} />
      <QuickCard label="Pub" item={nextAdBreak} onSelect={onSelect} />
    </div>
  )
})

const UpcomingList = memo(function UpcomingList({
  items,
  loading,
  onSelect,
}: {
  items: ScheduleItem[]
  loading: boolean
  onSelect: (item: ScheduleItem) => void
}) {
  return (
    <section className="mobile-operator__list-section">
      <div className="mobile-operator__list-heading">Prochains items</div>
      <ItemList items={items} loading={loading} onSelect={onSelect} emptyLabel="Aucun item à venir" />
    </section>
  )
})

const FilterButton = memo(function FilterButton({
  id,
  label,
  active,
  onFilter,
}: {
  id: MobileFilter
  label: string
  active: boolean
  onFilter: (filter: MobileFilter) => void
}) {
  const handleClick = useCallback(() => onFilter(id), [id, onFilter])
  return (
    <button type="button" onClick={handleClick} className={`mobile-operator__filter ${active ? 'is-active' : ''}`} aria-pressed={active}>
      {label}
    </button>
  )
})

const FilterBar = memo(function FilterBar({
  filter,
  onFilter,
  alertCount,
}: {
  filter: MobileFilter
  onFilter: (filter: MobileFilter) => void
  alertCount: number
}) {
  return (
    <div className="mobile-operator__filter-bar" aria-label="Filtres de grille">
      <FilterButton id="ALL" label="Tous" active={filter === 'ALL'} onFilter={onFilter} />
      <FilterButton id="PROGRAM" label="Programmes" active={filter === 'PROGRAM'} onFilter={onFilter} />
      <FilterButton id="LIVE" label="Directs" active={filter === 'LIVE'} onFilter={onFilter} />
      <FilterButton id="AD_BREAK" label="Pubs" active={filter === 'AD_BREAK'} onFilter={onFilter} />
      <FilterButton id="ALERTS" label={`Alertes${alertCount ? ` ${alertCount}` : ''}`} active={filter === 'ALERTS'} onFilter={onFilter} />
    </div>
  )
})

const ItemRow = memo(function ItemRow({ item, onSelect }: {
  item: ScheduleItem
  onSelect: (item: ScheduleItem) => void
}) {
  const handleClick = useCallback(() => onSelect(item), [item, onSelect])
  const statusStyle = {
    '--mobile-status-color': ITEM_STATUS_COLORS[item.status]?.badge ?? 'var(--color-muted)',
  } as DynamicStyle
  return (
    <button type="button" onClick={handleClick} className="mobile-operator__item-row">
      <span className="mobile-operator__status-dot" style={statusStyle} aria-hidden="true" />
      <span className="mobile-operator__item-main">
        <span className="mobile-operator__item-title">{itemTitle(item)}</span>
        <span className="mobile-operator__item-meta">{formatTime(item.startTime)} / {formatDurationShort(item.durationSec)} / {itemSubtitle(item)}</span>
      </span>
      <span className="mobile-operator__item-status">{statusLabel(item.status)}</span>
    </button>
  )
})

const ItemList = memo(function ItemList({
  items,
  loading,
  onSelect,
  emptyLabel = 'Aucun résultat',
}: {
  items: ScheduleItem[]
  loading: boolean
  onSelect: (item: ScheduleItem) => void
  emptyLabel?: string
}) {
  if (loading) return <div className="mobile-operator__empty" aria-live="polite"><Spinner /> Chargement...</div>
  if (!items.length) return <div className="mobile-operator__empty">{emptyLabel}</div>

  return (
    <div className="mobile-operator__item-list">
      {items.map(item => <ItemRow key={item.id} item={item} onSelect={onSelect} />)}
    </div>
  )
})

const AlertRow = memo(function AlertRow({ alert, onDismiss }: {
  alert: WatchdogAlert
  onDismiss: (alert: WatchdogAlert) => void
}) {
  const handleClick = useCallback(() => onDismiss(alert), [alert, onDismiss])
  return (
    <button
      type="button"
      onClick={handleClick}
      className={`mobile-operator__alert-row ${alert.severity === 'WARNING' ? 'is-warning' : 'is-danger'}`}
    >
      <span className="mobile-operator__alert-top">
        <strong>{alert.severity}</strong>
        <span>{new Date(alert.ts).toLocaleTimeString('fr-FR')}</span>
      </span>
      <span className="mobile-operator__alert-message">{alert.message}</span>
    </button>
  )
})

const AlertList = memo(function AlertList({
  alerts,
  onDismiss,
  onClear,
}: {
  alerts: WatchdogAlert[]
  onDismiss: (alert: WatchdogAlert) => void
  onClear: () => void
}) {
  return (
    <section className="mobile-operator__list-section">
      <div className="mobile-operator__alert-heading">
        <span>Alertes antenne</span>
        {alerts.length > 0 && <button type="button" onClick={onClear} className="mobile-operator__small-button">Tout retirer</button>}
      </div>
      {!alerts.length ? (
        <div className="mobile-operator__empty">Aucune alerte active</div>
      ) : (
        <div className="mobile-operator__alert-list">
          {alerts.map(alert => (
            <AlertRow key={`${alert.type}:${alert.itemId ?? 'global'}`} alert={alert} onDismiss={onDismiss} />
          ))}
        </div>
      )}
    </section>
  )
})

const ItemDetails = memo(function ItemDetails({ item, onClose }: { item: ScheduleItem; onClose: () => void }) {
  return (
    <div className="mobile-operator__sheet-backdrop" onClick={onClose}>
      <section
        className="mobile-operator__sheet"
        onClick={stopSheetPropagation}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-item-details-title"
      >
        <div className="mobile-operator__sheet-handle" aria-hidden="true" />
        <div className="mobile-operator__sheet-title-row">
          <div className="mobile-operator__sheet-heading">
            <div id="mobile-item-details-title" className="mobile-operator__sheet-title">{itemTitle(item)}</div>
            <div className="mobile-operator__sheet-meta">{itemSubtitle(item)}</div>
          </div>
          <button type="button" onClick={onClose} className="mobile-operator__close-button">Fermer</button>
        </div>
        <div className="mobile-operator__detail-grid">
          <Metric label="Début" value={formatTime(item.startTime)} />
          <Metric label="Fin" value={formatTime(item.endTime)} />
          <Metric label="Durée" value={formatDurationShort(item.durationSec)} />
          <Metric label="Statut" value={statusLabel(item.status)} />
        </div>
        {item.program?.summary && <p className="mobile-operator__summary">{item.program.summary}</p>}
        {item.program && (
          <div className="mobile-operator__detail-text">
            {item.program.presenter && <div>Présentation : {item.program.presenter}</div>}
            {item.program.director && <div>Réalisation : {item.program.director}</div>}
            {item.program.production && <div>Production : {item.program.production}</div>}
            {item.program.genre && <div>Genre : {item.program.genre}</div>}
          </div>
        )}
        {item.adPlacements?.length ? (
          <div className="mobile-operator__detail-text">
            {item.adPlacements.map((placement, index) => (
              <div key={placement.id}>{index + 1}. {placement.spot.title} / {placement.spot.durationSec}s</div>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  )
})

const ConfirmSheet = memo(function ConfirmSheet({
  action,
  currentItem,
  nextItem,
  manualInterludeActive,
  onCancel,
  onConfirm,
}: {
  action: ConfirmAction
  currentItem: ScheduleItem | null
  nextItem: ScheduleItem | null
  manualInterludeActive: boolean
  onCancel: () => void
  onConfirm: () => void | Promise<void>
}) {
  const title = action === 'skip' ? 'Confirmer le skip' : manualInterludeActive ? "Couper l'interlude manuel" : "Envoyer l'interlude"
  const body = action === 'skip'
    ? `Item courant : ${itemTitle(currentItem)}. Prochain item : ${itemTitle(nextItem)}.`
    : `Item courant : ${itemTitle(currentItem)}.`

  return (
    <div className="mobile-operator__sheet-backdrop" onClick={onCancel}>
      <section
        className="mobile-operator__confirm-sheet"
        onClick={stopSheetPropagation}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="mobile-confirm-title"
        aria-describedby="mobile-confirm-description"
      >
        <div id="mobile-confirm-title" className="mobile-operator__sheet-title">{title}</div>
        <p id="mobile-confirm-description" className="mobile-operator__confirm-text">{body}</p>
        <div className="mobile-operator__confirm-actions">
          <button type="button" onClick={onCancel} className="mobile-operator__cancel-button">Annuler</button>
          <button type="button" onClick={onConfirm} className="mobile-operator__confirm-button">Confirmer</button>
        </div>
      </section>
    </div>
  )
})
